/**
 * Remaining Contract Widget Component
 * Shows remaining contract amount and budget with visual indicators
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { DashboardMetrics, MonthlyData } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'

interface RemainingContractWidgetProps {
  metrics: DashboardMetrics
  monthlyData: MonthlyData[]
}

export function RemainingContractWidget({ metrics, monthlyData }: RemainingContractWidgetProps) {
  const { formatMoney, formatPercent } = useFormatters()

  const hasData = monthlyData && monthlyData.length > 0

  // Contract calculations
  const remainingContract = metrics.remainingContract
  const billedPercent = metrics.contractAmount > 0 
    ? (metrics.billedToDate / metrics.contractAmount) * 100 
    : 0
  const isOverContract = billedPercent > 100
  const contractRemainingPercent = metrics.contractAmount > 0
    ? (remainingContract / metrics.contractAmount) * 100
    : 0

  // Budget calculations
  const remainingBudget = metrics.projectedCost - metrics.costsToDate
  const budgetUsedPercent = metrics.projectedCost > 0
    ? (metrics.costsToDate / metrics.projectedCost) * 100
    : 0
  const isOverBudget = budgetUsedPercent > 100
  const budgetRemainingPercent = metrics.projectedCost > 0
    ? (remainingBudget / metrics.projectedCost) * 100
    : 0

  // Calculate average cost over last 6 months (or available months)
  const recentMonths = monthlyData.slice(-6)
  const avgCost6mo = recentMonths.length > 0
    ? recentMonths.reduce((sum, m) => sum + m.costs, 0) / recentMonths.length
    : 0

  return (
    <Card className="h-full">
      <CardHeader className="pb-2 py-3 px-4">
        <CardTitle className="text-lg">
          Remaining Contract Amount
        </CardTitle>
      </CardHeader>
      <CardContent className={hasData ? "space-y-3" : ""}>
        {!hasData ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        ) : (
          <>
        {/* Contract Section */}
        <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(37,99,235,0.04)' }}>
          {/* Section header with chip */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-1 h-3 rounded-full bg-[#2563EB]" />
              <span className="text-xs font-medium text-[#475569]">Contract</span>
            </div>
            <Badge className="bg-[#DBEAFE] text-[#2563EB] hover:bg-[#DBEAFE] text-[10px] font-medium px-2 py-0.5">
              Contract
            </Badge>
          </div>

          {/* Big number */}
          <p className="text-2xl font-bold text-[#0F172A] tracking-tight mb-3">
            {formatMoney(remainingContract)}
          </p>

          {/* Progress bar with percentage label */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    isOverContract ? 'bg-red-500' : 'bg-[#2563EB]'
                  }`}
                  style={{ width: `${Math.min(billedPercent, 100)}%` }}
                />
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${
                isOverContract ? 'text-red-500' : 'text-[#2563EB]'
              }`}>
                {formatPercent(Math.max(contractRemainingPercent, 0), 1)} remaining
              </span>
            </div>
            <p className="text-xs text-[#64748B]">
              {formatMoney(metrics.billedToDate)} billed of {formatMoney(metrics.contractAmount)}
            </p>
          </div>

          {/* 3-column mini-metrics row */}
          <div className="grid grid-cols-3 gap-3 pt-3 mt-3 border-t border-[#E2E8F0]">
            <div>
              <p className="text-[10px] text-[#94A3B8] mb-0.5">Billed to Date</p>
              <p className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(metrics.billedToDate)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] mb-0.5">Remaining %</p>
              <p className={`text-sm font-semibold ${isOverContract ? 'text-red-500' : 'text-[#0F172A]'}`}>
                {formatPercent(Math.max(contractRemainingPercent, 0), 1)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] mb-0.5">Contract Amount</p>
              <p className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(metrics.contractAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Budget Section */}
        <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(124,58,237,0.04)' }}>
          {/* Section header with chip */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-1 h-3 rounded-full bg-[#7C3AED]" />
              <span className="text-xs font-medium text-[#475569]">Budget</span>
            </div>
            <Badge className="bg-[#EDE9FE] text-[#7C3AED] hover:bg-[#EDE9FE] text-[10px] font-medium px-2 py-0.5">
              Budget
            </Badge>
          </div>

          {/* Big number */}
          <p className="text-2xl font-bold text-[#0F172A] tracking-tight mb-3">
            {formatMoney(remainingBudget)}
          </p>

          {/* Progress bar with percentage label */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    isOverBudget ? 'bg-red-500' : 'bg-[#7C3AED]'
                  }`}
                  style={{ width: `${Math.min(budgetUsedPercent, 100)}%` }}
                />
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${
                isOverBudget ? 'text-red-500' : 'text-[#7C3AED]'
              }`}>
                {formatPercent(Math.max(budgetRemainingPercent, 0), 1)} remaining
              </span>
            </div>
            <p className="text-xs text-[#64748B]">
              {formatMoney(metrics.costsToDate)} used of {formatMoney(metrics.projectedCost)}
            </p>
          </div>

          {/* 3-column mini-metrics row */}
          <div className="grid grid-cols-3 gap-3 pt-3 mt-3 border-t border-[#E2E8F0]">
            <div>
              <p className="text-[10px] text-[#94A3B8] mb-0.5">Used to Date</p>
              <p className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(metrics.costsToDate)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] mb-0.5">Remaining %</p>
              <p className={`text-sm font-semibold ${isOverBudget ? 'text-red-500' : 'text-[#0F172A]'}`}>
                {formatPercent(Math.max(budgetRemainingPercent, 0), 1)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[#94A3B8] mb-0.5">Avg Cost (6 mo)</p>
              <p className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(avgCost6mo)}
              </p>
            </div>
          </div>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  )
}

